import { Box, Typography, Chip, Skeleton, IconButton, Tooltip } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { getStatusColor, type UserFriendlyStatus } from '../../utils/statusMapping';

const STATUS_ICONS: Record<UserFriendlyStatus, string> = {
  'Unsubmitted': '📝',
  'Queued': '📋',
  'New Files': '📂',
  'In Progress': '⚙️',
  'Download to Process': '⬇️',
  'Done': '✅',
};

interface ColumnHeaderProps {
  status: UserFriendlyStatus;
  jobCount: number;
  isLoading: boolean;
  onCreateJob?: () => void;
}

export function ColumnHeader({ status, jobCount, isLoading, onCreateJob }: ColumnHeaderProps) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'grey.50',
        height: 60, // Further reduced to 60px for maximum compactness
        display: 'flex',
        alignItems: 'center', // Center everything vertically
        justifyContent: 'space-between', // Spread items across the width
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: '0.9rem', // Slightly smaller for compactness
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <span style={{ fontSize: '1rem' }}>{STATUS_ICONS[status]}</span>
          {status}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isLoading ? (
          <Skeleton variant="rectangular" width={28} height={20} sx={{ borderRadius: 3 }} />
        ) : (
          <Chip
            label={jobCount}
            size="small"
            sx={{
              fontWeight: 600,
              minWidth: 26,
              height: 20, // Smaller for compact header
              fontSize: '0.7rem',
              backgroundColor: getStatusColor(status),
              color: 'white',
            }}
          />
        )}

        {onCreateJob && (
          <Tooltip title="Create new job">
            <IconButton
              size="small"
              onClick={onCreateJob}
              sx={{
                width: 26,
                height: 26, // Smaller button for compact header
                color: 'primary.main',
                bgcolor: 'primary.50',
                '&:hover': {
                  bgcolor: 'primary.100',
                },
              }}
            >
              <AddIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
